import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Check, CheckCircle2, Copy, Loader2, Send } from "lucide-react";
import { useMemo, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { toast } from "sonner";

import { APIError, apiRequest } from "@/lib/api";
import { PRODUCT_NAME } from "@/lib/product";
import {
  getRequestAccessPromoCode,
  getRequestAccessSource,
  parseRequestAccessSearch,
} from "@/lib/request-access-attribution";

export const Route = createFileRoute("/request-access")({
  validateSearch: parseRequestAccessSearch,
  head: () => ({
    meta: [{ title: `Request Access To The Genrl Compute Wallet · ${PRODUCT_NAME}` }],
    links: [{ rel: "preload", as: "image", href: REQUEST_ACCESS_BACKGROUND }],
  }),
  component: RequestAccessPage,
});

const REQUEST_ACCESS_BACKGROUND = `${import.meta.env.BASE_URL}wallet-backgrounds/request-access-daniel-leone-1920.jpg`;

type RequestAccessForm = {
  name: string;
  email: string;
  company: string;
  jobTitle: string;
  computeUse: string;
};

const EMPTY_FORM: RequestAccessForm = {
  name: "",
  email: "",
  company: "",
  jobTitle: "",
  computeUse: "",
};

const REQUEST_ACCESS_SUCCESS_MESSAGE =
  "Thanks for requesting access, we will let you know when you have been granted access to Genrl.";

const REQUEST_ACCESS_PAGE_STYLE = {
  "--genrl-panel-bg": "rgba(255, 255, 252, 0.42)",
  "--genrl-panel-bg-strong": "rgba(255, 255, 252, 0.62)",
  "--genrl-panel-bg-soft": "rgba(255, 255, 252, 0.28)",
  "--genrl-panel-border": "rgba(255, 255, 255, 0.66)",
  "--genrl-panel-border-soft": "rgba(255, 255, 255, 0.46)",
  "--genrl-panel-ink": "#071f17",
  "--genrl-panel-ink-soft": "rgba(7, 31, 23, 0.66)",
  "--genrl-panel-ink-muted": "rgba(7, 31, 23, 0.48)",
  "--genrl-panel-accent": "#075c35",
  "--genrl-panel-accent-soft": "rgba(7, 92, 53, 0.09)",
  "--genrl-panel-shadow": "0 24px 70px rgba(54, 42, 24, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
} as CSSProperties & Record<string, string>;

function RequestAccessPage() {
  const search = Route.useSearch();
  const [form, setForm] = useState<RequestAccessForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [promoCopied, setPromoCopied] = useState(false);
  const promoCode = getRequestAccessPromoCode(search);

  const canSubmit = useMemo(
    () => Boolean(
      form.name.trim() &&
      form.email.trim() &&
      form.company.trim() &&
      form.jobTitle.trim() &&
      form.computeUse.trim() &&
      !submitting,
    ),
    [form, submitting],
  );

  const updateField = (key: keyof RequestAccessForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (submitted) setSubmitted(false);
    if (promoCopied) setPromoCopied(false);
  };

  const copyPromoCode = async () => {
    if (!promoCode) return;
    const copied = await copyTextToClipboard(promoCode);
    if (!copied) {
      toast.error("Could not copy promo code");
      return;
    }
    setPromoCopied(true);
    toast.success("Promo code copied");
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await apiRequest("/api/v1/request-access", {
        method: "POST",
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          job_title: form.jobTitle.trim(),
          compute_use: form.computeUse.trim(),
          page_url: typeof window === "undefined" ? "" : window.location.href,
          source: getRequestAccessSource(search),
        },
        timeoutMs: 12000,
      });
      setForm(EMPTY_FORM);
      setSubmitted(true);
      toast.success(REQUEST_ACCESS_SUCCESS_MESSAGE);
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail
          : error instanceof Error
            ? error.message
            : "Request access could not be submitted.";
      toast.error(message || "Request access could not be submitted.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="relative grid min-h-screen w-full place-items-center overflow-hidden px-4 py-10 font-sans text-[var(--genrl-panel-ink)] tracking-normal"
      style={REQUEST_ACCESS_PAGE_STYLE}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${REQUEST_ACCESS_BACKGROUND})`, backgroundPosition: "center 46%" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 bg-[linear-gradient(180deg,rgba(2,11,15,0.18),rgba(2,11,15,0.34)),linear-gradient(90deg,rgba(2,11,15,0.44),rgba(2,11,15,0.08)_50%,rgba(2,11,15,0.34))]"
      />

      {submitted ? (
        <section className="wallet-ops-panel relative z-10 w-full max-w-[420px] p-6 font-sans tracking-normal">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--genrl-panel-accent-soft)] text-[var(--genrl-panel-accent)]">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <p className="mt-5 font-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--genrl-panel-ink-muted)]">
            Request sent
          </p>
          <h1 className="mt-1.5 font-sans text-[18px] font-semibold leading-[1.18] tracking-normal text-[var(--genrl-panel-ink)]">
            Thanks for requesting access
          </h1>
          <p className="mt-3 font-sans text-[13px] font-medium leading-5 tracking-normal text-[var(--genrl-panel-ink-soft)]">
            We will let you know when you have been granted access to Genrl.
          </p>
          {promoCode ? (
            <div className="mt-5 rounded-[18px] border border-[var(--genrl-panel-border-soft)] bg-[var(--genrl-panel-bg-soft)] p-3.5">
              <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--genrl-panel-ink-muted)]">
                Your AllianceDAO promo code
              </p>
              <p className="mt-1.5 font-sans text-[12px] font-medium leading-5 tracking-normal text-[var(--genrl-panel-ink-soft)]">
                Copy and keep this code for when your wallet access is approved.
              </p>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-[14px] border border-[var(--genrl-panel-border-soft)] bg-[var(--genrl-panel-bg-strong)] px-3 py-2.5">
                <code className="min-w-0 select-all font-mono text-[12px] font-semibold tracking-[0.04em] text-[var(--genrl-panel-ink)]">
                  {promoCode}
                </code>
                <button
                  type="button"
                  onClick={() => void copyPromoCode()}
                  aria-label={promoCopied ? "Promo code copied" : `Copy promo code ${promoCode}`}
                  className="inline-flex min-h-8 shrink-0 items-center justify-center gap-1.5 rounded-full bg-[var(--genrl-panel-ink)] px-3 font-sans text-[11px] font-semibold tracking-normal text-[#f8f7f0] transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--genrl-panel-accent)]"
                >
                  {promoCopied ? (
                    <Check aria-hidden="true" className="h-3.5 w-3.5" />
                  ) : (
                    <Copy aria-hidden="true" className="h-3.5 w-3.5" />
                  )}
                  <span aria-live="polite">{promoCopied ? "Copied" : "Copy"}</span>
                </button>
              </div>
              <Link
                to="/early-access"
                target="_blank"
                rel="noreferrer"
                aria-label="Open early access in a new tab"
                className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-full bg-[var(--genrl-panel-ink)] px-4 font-sans text-[12px] font-semibold tracking-normal text-[#f8f7f0] transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--genrl-panel-accent)]"
              >
                <span>Open early access</span>
                <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />
              </Link>
            </div>
          ) : null}
        </section>
      ) : (
      <section className="wallet-ops-panel relative z-10 w-full max-w-[420px] p-5 font-sans tracking-normal">
        <div>
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--genrl-panel-ink-muted)]">GENRL</p>
          <h1 className="mt-1.5 font-sans text-[18px] font-semibold leading-[1.18] tracking-normal text-[var(--genrl-panel-ink)]">
            Request Access To The Genrl Compute Wallet
          </h1>
        </div>

        <form className="mt-4 grid gap-3" onSubmit={onSubmit}>
          <RequestAccessField
            id="request-access-name"
            label="Name"
            autoComplete="name"
            value={form.name}
            onChange={(value) => updateField("name", value)}
          />
          <RequestAccessField
            id="request-access-email"
            label="Email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(value) => updateField("email", value)}
          />
          <RequestAccessField
            id="request-access-company"
            label="Company"
            autoComplete="organization"
            value={form.company}
            onChange={(value) => updateField("company", value)}
          />
          <RequestAccessField
            id="request-access-job-title"
            label="Job title"
            autoComplete="organization-title"
            value={form.jobTitle}
            onChange={(value) => updateField("jobTitle", value)}
          />

          <RequestAccessTextarea
            id="request-access-compute-use"
            label="What do you currently use compute for?"
            value={form.computeUse}
            onChange={(value) => updateField("computeUse", value)}
          />

          <div className="mt-0.5 grid gap-2.5">
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-full bg-[var(--genrl-panel-ink)] px-5 font-sans text-[13px] font-semibold tracking-normal text-[#f8f7f0] shadow-[0_18px_36px_color-mix(in_srgb,var(--genrl-panel-ink)_18%,transparent)] transition disabled:cursor-not-allowed disabled:bg-[var(--genrl-panel-bg-strong)] disabled:text-[var(--genrl-panel-ink-muted)] disabled:shadow-none"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Request access
            </button>
            <p className="text-center font-sans text-[11px] leading-5 text-[var(--genrl-panel-ink-muted)]">
              By submitting this form, you acknowledge our{" "}
              <Link
                to="/privacy"
                className="font-semibold text-[var(--genrl-panel-ink-soft)] underline underline-offset-4"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </form>
      </section>
      )}
    </main>
  );
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to the selection-based copy fallback.
    }
  }
  if (typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

function RequestAccessField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  type?: string;
}) {
  return (
    <label className="grid gap-2" htmlFor={id}>
      <span className="font-sans text-[12px] font-semibold tracking-normal text-[var(--genrl-panel-ink)]">{label}</span>
      <input
        id={id}
        required
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-10 w-full rounded-[16px] border border-[var(--genrl-panel-border-soft)] bg-[color-mix(in_srgb,var(--genrl-panel-bg-soft)_74%,transparent)] px-3.5 font-sans text-[13px] font-medium tracking-normal text-[var(--genrl-panel-ink)] outline-none transition placeholder:text-[var(--genrl-panel-ink-muted)] focus:border-[var(--genrl-panel-accent)]"
      />
    </label>
  );
}

function RequestAccessTextarea({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-2" htmlFor={id}>
      <span className="font-sans text-[12px] font-semibold tracking-normal text-[var(--genrl-panel-ink)]">{label}</span>
      <textarea
        id={id}
        required
        rows={3}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[86px] w-full resize-none rounded-[16px] border border-[var(--genrl-panel-border-soft)] bg-[color-mix(in_srgb,var(--genrl-panel-bg-soft)_74%,transparent)] px-3.5 py-2.5 font-sans text-[13px] font-medium leading-5 tracking-normal text-[var(--genrl-panel-ink)] outline-none transition placeholder:text-[var(--genrl-panel-ink-muted)] focus:border-[var(--genrl-panel-accent)]"
      />
    </label>
  );
}
